import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import styles from './AuditPage.module.css';
import Navbar, { BrandIcon } from '../components/Navbar';
import { toast } from 'react-toastify';
import { getApiUrl } from '../api';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const AuditPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [business, setBusiness] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [auditRequestId, setAuditRequestId] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchForm, setShowSearchForm] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isStartingScrape, setIsStartingScrape] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [modalState, setModalState] = useState(null); // null | 'loading' | 'success'
  const abortRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch(getApiUrl('/api/audit/create-audit-request'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ business, city, state, googleMapsUrl }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Audit request submitted successfully');
        setAuditRequestId(data.auditRequest || null);
        setSearchResults(data.searchResults || []);
        setSearchError(data.searchError || '');
        setShowSearchForm(!(data.searchResults && data.searchResults.length > 0));
        setBusiness('');
        setCity('');
        setState('');
        setGoogleMapsUrl('');
      } else {
        toast.error(`Error: ${data.message || 'Submission failed'}`);
      }
    } catch (err) {
      toast.error(`Network error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartScraping = async (place) => {
    if (!auditRequestId) {
      toast.error('No audit request available for scraping.');
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setIsStartingScrape(true);
    setModalState('loading');
    try {
      const payload = {
        auditRequestId,
        locationUrl: place.locationUrl,
        selectedPlace: {
          name:             place.name             || undefined,
          address:          place.address          || undefined,
          rating:           place.rating           ?? undefined,
          userRatingsTotal: place.userRatingsTotal ?? undefined,
          photoUrl:         place.photoUrl         || undefined,
          placeId:          place.placeId          || undefined,
        },
      };

      const response = await fetch( getApiUrl('/api/audit/start-scraping'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      const data = await response.json();
      if (response.ok) {
        setModalState('success');
      } else {
        setModalState(null);
        const errMsg = data.message || (data.errors && data.errors.map((e) => e.msg).join(', ')) || 'Unable to start scraping';
        toast.error(`Error: ${errMsg}`);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // User cancelled — don't show an error
        return;
      }
      setModalState(null);
      toast.error(`Network error: ${err.message}`);
    } finally {
      setIsStartingScrape(false);
      abortRef.current = null;
    }
  };

  const handleCancelScraping = async () => {
    // Abort the held fetch immediately
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setModalState(null);
    setIsStartingScrape(false);

    // Tell the backend to release and cancel
    if (auditRequestId) {
      try {
        await fetch(getApiUrl('/api/audit/cancel-scraping'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ auditRequestId }),
        });
      } catch (err) {
        console.warn('Cancel request failed:', err);
      }
    }
  };

  return (
    <div className={styles.page}>
      {/* Navbar */}
      <Navbar className={styles.navbar} innerClassName={styles.navInner} brandClassName={styles.navBrand} brand={<><BrandIcon size={28} />ReplyShoot</>}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <LanguageToggle />
            <Link to="/" className={styles.backLink}>← {t('home', 'Back to home')}</Link>
          </div>
      </Navbar>

      {/* Enterprise Persuasion Banner */}
      <section className={styles.persuasionBanner}>
        <div className={styles.persuasionInner}>
          <div className={styles.persuasionBadge}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            {t('trustedBy', 'Built for local businesses, big and small')}
          </div>
          <h2 className={styles.persuasionTitle}>
            {t('persuasionTitle', 'Your reviews are your reputation.')}<br />
            <span className={styles.persuasionAccent}>{t('persuasionTitleAccent', "Don't let them go unanswered.")}</span>
          </h2>
          <p className={styles.persuasionDesc}>
            {t('persuasionDesc', '88% of customers choose businesses that actively reply to reviews. Every silent review is a direct invitation for your competitor to win that customer instead.')}
          </p>
          <div className={styles.persuasionStats}>
            <div className={styles.persuasionStat}>
              <span className={styles.persuasionStatNum}>35%</span>
              <span className={styles.persuasionStatLabel}>{t('avgSalesLift', 'average sales lift (Womply)')}</span>
            </div>
            <div className={styles.persuasionStatDivider} />
            <div className={styles.persuasionStat}>
              <span className={styles.persuasionStatNum}>24hr</span>
              <span className={styles.persuasionStatLabel}>{t('guaranteedResponse', 'typical response time')}</span>
            </div>
            <div className={styles.persuasionStatDivider} />
            <div className={styles.persuasionStat}>
              <span className={styles.persuasionStatNum}>$15</span>
              <span className={styles.persuasionStatLabel}>{t('perMonthToStart', 'per month to start')}</span>
            </div>
          </div>
          <div className={styles.persuasionFeatures}>
            <span className={styles.persuasionFeature}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
              {t('writtenByExperts', 'Personalized to every review — never a generic template')}
            </span>
            <span className={styles.persuasionFeature}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
              {t('youApproveEveryReply', "You approve every reply before it's posted")}
            </span>
            <span className={styles.persuasionFeature}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
              {t('zeroContracts', 'Zero contracts · Cancel anytime')}
            </span>
          </div>
          <Link to="/onboarding" className={styles.persuasionCta}>
            {t('getStarted', 'Get Started')}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </Link>
          <p className={styles.persuasionCtaNote}>{t('first7DaysFree', 'Instant setup in under 15 minutes. Cancel anytime.')}</p>
        </div>
      </section>

      {/* Main */}
      <main className={`${styles.main} ${!showSearchForm && searchResults.length > 0 ? styles.mainWide : ''}`}>
        <div className={styles.iconWrap}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m21 21-4.34-4.34" /><circle cx="11" cy="11" r="8" />
          </svg>
        </div>
        <h1 className={styles.heroTitle}>{t('freeReviewAudit', 'Free Review Audit')}</h1>
        <p className={styles.heroSub}>
          {t('auditHeroSub', "See how many of your Google reviews are unanswered, and preview AI-drafted replies for each one.")}
        </p>

        {showSearchForm && (
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label className={styles.label}>{t('businessNameLabel', 'Business Name')}</label>
              <input
                id="audit-business"
                type="text" required
                className={styles.input}
                placeholder={t('enterBusinessName', "e.g. Joe's Pizza, Smile Dental Group")}
                value={business}
                onChange={e => setBusiness(e.target.value)}
              />
            </div>
            <div className={styles.twoCol}>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t('cityLabel', 'City')}</label>
                <input
                  id="audit-city"
                  type="text" required
                  className={styles.input}
                  placeholder="e.g. Austin"
                  value={city}
                  onChange={e => setCity(e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t('stateLabel', 'State')}</label>
                <select
                  id="audit-state"
                  required className={styles.select}
                  value={state}
                  onChange={e => setState(e.target.value)}
                >
                  <option value="">{t('selectState', 'Select...')}</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <button id="audit-submit" type="submit" className={styles.submitBtn} disabled={isLoading}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m21 21-4.34-4.34" /><circle cx="11" cy="11" r="8" />
              </svg>
              {isLoading ? t('searching', 'Searching...') : t('findMyBusiness', 'Find My Business')}
            </button>
            <p className={styles.formNote}>{t('noSignupRequired', "No sign-up required. We'll find your business on Google Maps.")}</p>
          </form>
        )}

        {searchError && (
          <div className={styles.errorBanner}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 14l2-2 2 2" /><path d="M12 8v4" /><circle cx="12" cy="17" r="1" /></svg>
            {searchError}
          </div>
        )}

        {searchResults.length > 0 && !showSearchForm && (
          <section className={styles.results}>
            <div className={styles.resultsHeader}>
              <h2 className={styles.resultsTitle}>{t('selectYourBusiness', 'Select your business')}</h2>
              <button
                type="button"
                className={styles.toggleSearchBtn}
                onClick={() => setShowSearchForm(true)}
              >
                <span className={styles.toggleIcon}>←</span> {t('searchAgain', 'Search again')}
              </button>
            </div>
            <div className={styles.resultsGrid}>
              {searchResults.map((place) => (
                <button
                  key={place.locationUrl}
                  type="button"
                  className={styles.placeCard}
                  onClick={() => handleStartScraping(place)}
                  disabled={isStartingScrape}
                >
                  {place.photoUrl ? (
                    <img
                      className={styles.placePhoto}
                      src={place.photoUrl}
                      alt={place.name}
                    />
                  ) : (
                    <div className={styles.placePhotoPlaceholder} />
                  )}
                  <div className={styles.placeCardBody}>
                    <div className={styles.placeCardHeader}>
                      <span className={styles.placeName}>{place.name}</span>
                      {place.rating != null && (
                        <span className={styles.placeRating}>{place.rating.toFixed(1)}</span>
                      )}
                    </div>
                    <p className={styles.placeAddress}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{flexShrink:0, opacity:0.6}}>
                        <path d="M20 10c0 6-8 13-8 13S4 16 4 10a8 8 0 0 1 16 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      {place.address}
                    </p>
                    <div className={styles.placeMeta}>
                      <span>{place.userRatingsTotal ? `${place.userRatingsTotal} reviews` : 'Reviews unavailable'}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}
      
      </main>

      {modalState && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            {modalState === 'loading' ? (
              <>
                <div className={styles.modalIconWrap} style={{ background: '#f3e8ff', boxShadow: '0 0 0 8px rgba(146, 25, 166, 0.1)' }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9219a6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <h2 className={styles.modalTitle}>{t('analyzingReviews', 'Analyzing Your Reviews')}</h2>
                <p className={styles.modalDesc}>
                  {t('analyzingSub', "We're scanning your Google reviews and generating AI-powered insights. This typically takes 1–3 minutes.")}
                </p>
                <div className={styles.progressBarWrap}>
                  <div className={styles.progressBar} key={auditRequestId} />
                </div>
                <button className={styles.btnSecondary} onClick={handleCancelScraping}>
                  {t('cancel', 'Cancel')}
                </button>
              </>
            ) : (
              <>
                <div className={styles.modalIconWrap}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <h2 className={styles.modalTitle}>{t('analysisComplete', 'Analysis Complete!')}</h2>
                <p className={styles.modalDesc}>
                  {t('analysisCompleteSub', 'Your reputation audit is ready. Click below to view your detailed report with AI-suggested responses.')}
                </p>
                <button
                  className={styles.modalBtn}
                  onClick={() => {
                    setModalState(null);
                    navigate(`/audit/${auditRequestId}`);
                  }}
                >
                  {t('viewReport', 'View Report')}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AuditPage;
